import React, { useState } from 'react'
import styled from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'
import IWTypography from './thunderbolt/IWTypography'
import { AvailableColors } from '../../@types/styled'

export type AnnouncementBarProps = {
  /**
   * Unique key for the announcement to track dismissal in localStorage
   */
  announcementKey: string
  /**
   * The announcement message to display
   */
  message: string
  /**
   * Color variant from theme (defaults to 'secondary')
   */
  color?: AvailableColors
  /**
   * Callback function called when announcement bar is dismissed
   */
  onDismiss?: () => void
}

const StyledAnnouncementBar = styled.div<{
  color: AvailableColors
}>`
  width: 100%;
  background: ${(props) => props.theme.palette[props.color][500]};
  color: ${(props) => props.theme.palette.grey[0]};
  padding: 0.75rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  flex: 1;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.125rem;
`

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${(props) => props.theme.palette.grey[0]};
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.9;
  transition: opacity 0.2s ease;
  font-size: 1rem;
  line-height: 1;
  border-radius: 0.25rem;

  &:hover {
    opacity: 1;
    background: rgba(255, 255, 255, 0.1);
  }

  &:focus {
    outline: 2px solid ${(props) => props.theme.palette.grey[0]};
    outline-offset: 2px;
  }
`

/**
 * AnnouncementBar component to display important announcements at the top of the application.
 * Can be dismissed by the user, and the dismissal state is stored in localStorage.
 */
const AnnouncementBar = ({
  announcementKey,
  message,
  color = 'primary',
  onDismiss,
}: AnnouncementBarProps) => {
  const storageKey = `announcement-dismissed-${announcementKey}`

  // Check if the announcement has been dismissed
  const [isDismissed, setIsDismissed] = useState<boolean>(
    localStorage.getItem(storageKey) === 'true',
  )

  if (isDismissed) {
    return null
  }

  const handleDismiss = () => {
    localStorage.setItem(storageKey, 'true')
    setIsDismissed(true)
    if (onDismiss) {
      onDismiss()
    }
  }

  return (
    <StyledAnnouncementBar color={color} data-testid="announcement-bar">
      <ContentWrapper>
        <IWTypography
          weight="medium"
          size="sm"
          inheritFontColor
          as="a"
          href="https://gridx.com/gridx-acquires-innowatts"
          target="_blank"
        >
          {message}
        </IWTypography>
      </ContentWrapper>
      <CloseButton
        onClick={handleDismiss}
        aria-label="Dismiss announcement"
        data-testid="announcement-dismiss-button"
      >
        <FontAwesomeIcon icon={faXmark} />
      </CloseButton>
    </StyledAnnouncementBar>
  )
}

export default AnnouncementBar
